import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import {
  FEEDBACK_CATEGORIES, FEEDBACK_PRIORITIES, FEEDBACK_STATUSES,
  feedbackCategoryLabel, feedbackPriorityLabel,
} from '../../data/feedbackConstants';
import FeedbackStatusBadge from './FeedbackStatusBadge';
import { Download, RefreshCw, MessageSquare } from 'lucide-react';
import { exportFeedbackXlsx, getApiErrorMessage } from '../../utils/feedbackDownload';

function StatCard({ title, value, statusKey, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${
        active ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20' : 'border-border bg-card'
      }`}
    >
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className="text-2xl font-bold mt-1">{value ?? 0}</p>
    </button>
  );
}

export default function FeedbackManagement({ scope = 'platform' }) {
  const isSchoolScope = scope === 'school';
  const isCrmScope = scope === 'crm';
  const listBasePath = isSchoolScope ? '/school-feedback' : isCrmScope ? '/crm/feedback' : '/feedback';
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [counts, setCounts] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ page: 1, pages: 1, total: 0 });

  const statusFilter = searchParams.get('status') || '';
  const categoryFilter = searchParams.get('category') || '';
  const priorityFilter = searchParams.get('priority') || '';
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '');

  const buildParams = (page = 1) => {
    const params = { page, limit: 20 };
    if (statusFilter) params.status = statusFilter;
    if (categoryFilter) params.category = categoryFilter;
    if (priorityFilter) params.priority = priorityFilter;
    if (search.trim()) params.q = search.trim();
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  const fetchStats = () => {
    api.get('/feedback/stats')
      .then((res) => setCounts(res.data?.data?.counts || {}))
      .catch(() => setCounts({}));
  };

  const fetchItems = (page = 1) => {
    setLoading(true);
    api.get('/feedback', { params: buildParams(page) })
      .then((res) => {
        setItems(res.data?.data?.items || []);
        setMeta(res.data?.data?.meta || { page: 1, pages: 1, total: 0 });
      })
      .catch((e) => toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchItems(1); }, [statusFilter, categoryFilter, priorityFilter, dateFrom, dateTo]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const handleExport = async () => {
    try {
      await exportFeedbackXlsx(new URLSearchParams(buildParams()));
      toast({ title: 'Export complete', description: 'Feedback export saved.' });
    } catch (e) {
      toast({ title: 'Export failed', description: await getApiErrorMessage(e), variant: 'destructive' });
    }
  };

  const statCards = [
    { key: '', title: 'Total Feedback', countKey: 'total' },
    ...FEEDBACK_STATUSES.map((s) => ({ key: s.value, title: s.label, countKey: s.value })),
  ];

  return (
    <div className="space-y-6" data-testid="feedback-management">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <MessageSquare className="h-7 w-7" />
            Feedback Management
          </h1>
          <p className="text-muted-foreground">
            {isSchoolScope
              ? 'Review and manage feedback submitted to your school'
              : isCrmScope
                ? 'Feedback assigned to you for review and follow-up'
                : 'Review and manage software feedback across all schools'}
          </p>
        </div>
        {!isSchoolScope && !isCrmScope && (
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
        {statCards.map((card) => (
          <StatCard
            key={card.key || 'total'}
            title={card.title}
            value={counts[card.countKey]}
            statusKey={card.key}
            active={statusFilter === card.key}
            onClick={() => setFilter('status', card.key)}
          />
        ))}
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="Search feedback..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-56"
              onKeyDown={(e) => e.key === 'Enter' && fetchItems(1)}
            />
            <Select value={categoryFilter || 'all'} onValueChange={(v) => setFilter('category', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {FEEDBACK_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter || 'all'} onValueChange={(v) => setFilter('priority', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                {FEEDBACK_PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateInput value={dateFrom} onChange={setDateFrom} placeholder="From date" className="w-[140px]" />
            <DateInput value={dateTo} onChange={setDateTo} placeholder="To date" className="w-[140px]" />
            <Button variant="outline" size="icon" onClick={() => { fetchStats(); fetchItems(meta.page); }} aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <p className="text-muted-foreground py-8 text-center">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-muted-foreground py-8 text-center">No feedback found.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="pb-3 pr-3 font-medium">Feedback ID</th>
                    {!isSchoolScope && <th className="pb-3 pr-3 font-medium">School</th>}
                    <th className="pb-3 pr-3 font-medium">User</th>
                    <th className="pb-3 pr-3 font-medium">Role</th>
                    <th className="pb-3 pr-3 font-medium">Title</th>
                    <th className="pb-3 pr-3 font-medium">Category</th>
                    <th className="pb-3 pr-3 font-medium">Priority</th>
                    <th className="pb-3 pr-3 font-medium">Status</th>
                    {!isSchoolScope && !isCrmScope && <th className="pb-3 pr-3 font-medium">Assigned to</th>}
                    <th className="pb-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="border-b border-border/60 hover:bg-muted/40">
                      <td className="py-3 pr-3">
                        <Link to={`${listBasePath}/${row.id}`} className="font-mono text-blue-600 hover:underline">
                          {row.feedback_code}
                        </Link>
                      </td>
                      {!isSchoolScope && <td className="py-3 pr-3 max-w-[120px] truncate">{row.school_name}</td>}
                      <td className="py-3 pr-3">{row.user_name}</td>
                      <td className="py-3 pr-3 capitalize">{row.user_role_slug?.replace(/_/g, ' ')}</td>
                      <td className="py-3 pr-3 max-w-[160px] truncate">{row.title}</td>
                      <td className="py-3 pr-3">{feedbackCategoryLabel(row.category)}</td>
                      <td className="py-3 pr-3 capitalize">{feedbackPriorityLabel(row.priority)}</td>
                      <td className="py-3 pr-3"><FeedbackStatusBadge status={row.status} /></td>
                      {!isSchoolScope && !isCrmScope && <td className="py-3 pr-3 max-w-[140px] truncate">{row.assigned_to_name || '—'}</td>}
                      <td className="py-3 text-muted-foreground whitespace-nowrap">{row.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {meta.pages > 1 && (
            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" disabled={meta.page <= 1} onClick={() => fetchItems(meta.page - 1)}>Previous</Button>
              <span className="text-sm text-muted-foreground self-center">Page {meta.page} of {meta.pages}</span>
              <Button variant="outline" size="sm" disabled={meta.page >= meta.pages} onClick={() => fetchItems(meta.page + 1)}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
